import React, { useState, useEffect } from 'react';
import { Mail, Users, Send, Download, Trash2, Eye } from 'lucide-react';
import api from '../../services/api';

const Newsletter = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [newsletterForm, setNewsletterForm] = useState({
    subject: '',
    content: ''
  });
  const [sendingNewsletter, setSendingNewsletter] = useState(false);

  // Aboneleri yükle
  const loadSubscribers = async (page = 1) => {
    try {
      setLoading(true);
      const response = await api.get(`/newsletter/subscribers?page=${page}&limit=20`);
      
      if (response.data.success) {
        setSubscribers(response.data.subscribers);
        setPagination(response.data.pagination);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('Abone listesi yüklenemedi:', error);
      alert('Abone listesi yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Newsletter gönder
  const sendNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterForm.subject || !newsletterForm.content) {
      alert('Konu ve içerik gerekli');
      return;
    }

    if (!window.confirm('Newsletter tüm abonelere gönderilecek. Emin misiniz?')) {
      return;
    }

    try {
      setSendingNewsletter(true);
      const response = await api.post('/newsletter/send-newsletter', newsletterForm);
      
      if (response.data.success) {
        alert(`Newsletter gönderildi! Başarılı: ${response.data.stats.success}, Hata: ${response.data.stats.errors}`);
        setNewsletterForm({ subject: '', content: '' });
      } else {
        alert('Newsletter gönderilemedi');
      }
    } catch (error) {
      console.error('Newsletter gönderim hatası:', error);
      alert('Newsletter gönderilemedi');
    } finally {
      setSendingNewsletter(false);
    }
  };

  // CSV indir
  const downloadCSV = async () => {
    try {
      const response = await api.get('/newsletter/subscribers?limit=1000');
      if (response.data.success) {
        const csvContent = [
          'E-posta,Abone Tarihi,Durum',
          ...response.data.subscribers.map(sub => 
            `${sub.email},${new Date(sub.subscribed_at).toLocaleDateString('tr-TR')},${sub.status}`
          )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `newsletter_aboneleri_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('CSV indirme hatası:', error);
      alert('CSV indirilemedi');
    }
  };

  useEffect(() => {
    loadSubscribers();
  }, []);

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Newsletter Yönetimi</h1>
        <p className="text-gray-600">Aboneleri yönetin ve newsletter gönderin</p>
      </div>

      {/* İstatistikler */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Toplam Abone</p>
              <p className="text-2xl font-bold text-gray-900">{pagination.total_count || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Mail className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Bu Sayfa</p>
              <p className="text-2xl font-bold text-gray-900">{subscribers.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Send className="h-8 w-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Sayfa</p>
              <p className="text-2xl font-bold text-gray-900">{currentPage} / {pagination.total_pages || 1}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Abone Listesi */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Abone Listesi</h2>
              <button
                onClick={downloadCSV}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="h-4 w-4 mr-2" />
                CSV İndir
              </button>
            </div>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-500">Yükleniyor...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {subscribers.map((subscriber) => (
                  <div key={subscriber.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center">
                      <Mail className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">{subscriber.email}</p>
                        <p className="text-sm text-gray-500">
                          {new Date(subscriber.subscribed_at).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      subscriber.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {subscriber.status === 'active' ? 'Aktif' : 'Pasif'}
                    </span>
                  </div>
                ))}

                {subscribers.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>Henüz abone yok</p>
                  </div>
                )}
              </div>
            )}

            {/* Sayfalama */}
            {pagination.total_pages > 1 && (
              <div className="mt-6 flex justify-center space-x-2">
                <button
                  onClick={() => loadSubscribers(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Önceki
                </button>
                <span className="px-3 py-2 text-gray-700">
                  {currentPage} / {pagination.total_pages}
                </span>
                <button
                  onClick={() => loadSubscribers(currentPage + 1)}
                  disabled={currentPage === pagination.total_pages}
                  className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Sonraki
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Newsletter Gönder */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Newsletter Gönder</h2>
          </div>

          <form onSubmit={sendNewsletter} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Konu *
              </label>
              <input
                type="text"
                value={newsletterForm.subject}
                onChange={(e) => setNewsletterForm({...newsletterForm, subject: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Newsletter konusu"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                İçerik *
              </label>
              <textarea
                value={newsletterForm.content}
                onChange={(e) => setNewsletterForm({...newsletterForm, content: e.target.value})}
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="HTML içerik (örnek: <h1>Başlık</h1><p>İçerik</p>)"
                required
              />
            </div>

            <button
              type="submit"
              disabled={sendingNewsletter}
              className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sendingNewsletter ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Gönderiliyor...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Newsletter Gönder
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Newsletter;
